import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RiShoppingBagLine, RiDeleteBin6Line, RiHeartLine, RiArrowRightUpLine } from 'react-icons/ri';
import { useAppLanguage } from '../../Hooks/useAppLanguage';
import { useCart } from '../../Context/CartContext';
import { getCategoryLabel } from '../../Utils/categoryI18n';
import ProductCardImage from '../../Component/UI/ProductCard/ProductCardImage';
import { usePublicSettings } from '../../Context/PublicSettingsContext';
import { getEffectivePrice } from '../../Utils/productPricing';
import './Wishlist.css';

const resolveItemName = (name, lang) => {
    if (name && typeof name === 'object') {
        return name[lang] || name.en || Object.values(name)[0] || '';
    }
    return String(name || '');
};

function Wishlist() {
    const { t, currentLanguage, dir, langPath } = useAppLanguage();
    const navigate = useNavigate();
    const { wishlistItems, removeFromWishlist, moveToCart } = useCart();
    const { formatPrice } = usePublicSettings();

    const handleAddToCart = (item) => {
        const id = item._id || item.id;
        const hasVariants = Array.isArray(item.variants) && item.variants.length > 0;
        if (hasVariants) {
            navigate(langPath(`/product-details/${id}`));
            return;
        }
        moveToCart(id);
    };

    return (
        <main className="main">
            <section className="wishlist-page">
                <div className="container" dir={dir}>
                    {wishlistItems.length === 0 ? (
                        <div className="wishlist-empty">
                            <div className="wishlist-empty-visual" aria-hidden="true">
                                <RiHeartLine className="wishlist-empty-heart" />
                            </div>
                            <h2 className="wishlist-empty-title">{t('wishlist.empty')}</h2>
                            <p className="wishlist-empty-text">{t('wishlist.emptyDescription')}</p>
                            <Link to={langPath('/products')} className="wishlist-btn wishlist-btn--primary">
                                <span>{t('wishlist.browseProducts')}</span>
                                <RiArrowRightUpLine className="wishlist-btn-ico" aria-hidden />
                                <span className="wishlist-btn-shine" aria-hidden />
                            </Link>
                        </div>
                    ) : (
                        <>
                            <p className="wishlist-summary" role="status">
                                {t('wishlist.summary', { count: wishlistItems.length })}
                            </p>
                            <ul className="wishlist-list">
                                {wishlistItems.map((item) => {
                                    const id = item._id || item.id;
                                    const name = resolveItemName(item.name, currentLanguage);
                                    const detailPath = langPath(`/product-details/${id}`);
                                    const categoryLabel = getCategoryLabel(item.category, currentLanguage);
                                    const hasVariants = Array.isArray(item.variants) && item.variants.length > 0;

                                    return (
                                        <li key={id}>
                                            <article className="wishlist-card">
                                                <div className="wishlist-card-media">
                                                    <Link to={detailPath} className="wishlist-card-media-link">
                                                        <ProductCardImage
                                                            product={item}
                                                            productName={name}
                                                            alt={name}
                                                            className="wishlist-card-img"
                                                        />
                                                    </Link>
                                                </div>
                                                <div className="wishlist-card-main">
                                                    <h2 className="wishlist-card-title">
                                                        <Link to={detailPath} className="wishlist-card-title-link">
                                                            {name}
                                                        </Link>
                                                    </h2>
                                                    {categoryLabel ? (
                                                        <p className="wishlist-card-category">{categoryLabel}</p>
                                                    ) : null}
                                                    <p className="wishlist-card-price">
                                                        {formatPrice(getEffectivePrice(item))}
                                                    </p>
                                                </div>
                                                <div className="wishlist-card-actions">
                                                    <button
                                                        type="button"
                                                        className="wishlist-btn wishlist-btn--primary wishlist-btn--compact"
                                                        onClick={() => handleAddToCart(item)}
                                                    >
                                                        <RiShoppingBagLine className="wishlist-btn-lead" aria-hidden />
                                                        <span>
                                                            {hasVariants ? t('wishlist.selectOptions') : t('wishlist.addToCart')}
                                                        </span>
                                                        <span className="wishlist-btn-shine" aria-hidden />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="wishlist-btn-remove"
                                                        onClick={() => removeFromWishlist(id)}
                                                        aria-label={t('wishlist.remove')}
                                                    >
                                                        <RiDeleteBin6Line aria-hidden />
                                                    </button>
                                                </div>
                                            </article>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
            </section>
        </main>
    );
}

export default Wishlist;
